import { useCallback, useEffect, useState } from 'react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { dictionariesApi } from '@/api/dictionaries'
import { DictionaryForm } from '@/components/dictionaries/DictionaryForm'
import { DictionaryCard } from '@/components/dictionaries/DictionaryCard'
import type { Dictionary, CreateDictionaryData, UpdateDictionaryData } from '@/types'
import { ROUTES } from '@/utils/constants'
import { formatDateDMY } from '@/utils/date'
import {
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  TablePagination,
  useMediaQuery,
  Breadcrumbs,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import Link from '@mui/material/Link'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import '@/styles/dashboard.css'

export function DictionariesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [dictionaries, setDictionaries] = useState<Dictionary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    per_page: 20,
  })
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedDictionary, setSelectedDictionary] = useState<Dictionary | null>(null)
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null)
  const [actionsDictionary, setActionsDictionary] = useState<Dictionary | null>(null)

  const fetchDictionaries = useCallback(
    (overrides?: { page?: number; per_page?: number }) => {
      setIsLoading(true)
      setError(null)
      const page = overrides?.page ?? pagination.currentPage
      const per_page = overrides?.per_page ?? pagination.per_page
      dictionariesApi
        .getDictionaries({
          search: appliedSearch || undefined,
          page,
          per_page,
        })
        .then((res) => {
          setDictionaries(res.data)
          setPagination({
            currentPage: res.current_page,
            lastPage: res.last_page,
            total: res.total,
            per_page: res.per_page ?? pagination.per_page,
          })
        })
        .catch((err: Error & { response?: { data?: { message?: string } } }) => {
          setError(err.response?.data?.message || err.message || 'Ошибка загрузки словарей')
        })
        .finally(() => setIsLoading(false))
    },
    [appliedSearch, pagination.currentPage, pagination.per_page]
  )

  useEffect(() => {
    const timer = window.setTimeout(() => setAppliedSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    fetchDictionaries({ page: 1 })
  }, [fetchDictionaries])

  const handlePageChange = (_: unknown, newPage: number) => {
    fetchDictionaries({ page: newPage + 1 })
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const perPage = parseInt(e.target.value, 10)
    setPagination((p) => ({ ...p, per_page: perPage }))
    fetchDictionaries({ page: 1, per_page: perPage })
  }

  const handleCreateDictionary = async (data: CreateDictionaryData | UpdateDictionaryData) => {
    try {
      const created = await dictionariesApi.createDictionary({
        name: data.name!,
        description: data.description ?? undefined,
      })
      setIsCreateModalOpen(false)
      navigate(ROUTES.DICTIONARY_VIEW(created.id))
    } catch (err) {
      const e = err as Error & { response?: { status?: number; data?: { message?: string } } }
      if (e.response?.status === 403) {
        setError(e.response?.data?.message || t('dictionaries.subscriptionLimit'))
      } else {
        setError(e.response?.data?.message || e.message || 'Ошибка создания словаря')
      }
      throw e
    }
  }

  const handleEditDictionary = async (data: UpdateDictionaryData) => {
    if (!selectedDictionary) return
    await dictionariesApi.updateDictionary(selectedDictionary.id, data)
    setIsEditModalOpen(false)
    setSelectedDictionary(null)
    fetchDictionaries()
  }

  const handleDeleteDictionary = async (dictionary: Dictionary) => {
    if (!window.confirm(t('dictionaries.deleteConfirm'))) return
    try {
      await dictionariesApi.deleteDictionary(dictionary.id)
      setActionsDictionary(null)
      setActionsAnchorEl(null)
      fetchDictionaries()
    } catch (err) {
      const e = err as Error
      setError(e.message || 'Ошибка удаления словаря')
    }
  }

  const openActionsMenu = (event: React.MouseEvent<HTMLElement>, dictionary: Dictionary) => {
    event.stopPropagation()
    setActionsAnchorEl(event.currentTarget)
    setActionsDictionary(dictionary)
  }

  const closeActionsMenu = () => {
    setActionsAnchorEl(null)
    setActionsDictionary(null)
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            component={RouterLink}
            to={ROUTES.DASHBOARD}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dictionaries.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('dictionaries.title')}
          </Typography>
          <PageHelpPopover pageKey="dictionaries" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('dictionaries.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 120 : 200, flex: isMobile ? 1 : undefined }}
          />
          <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateModalOpen(true)}
              sx={{ height: 40 }}
            >
              {t('dictionaries.create')}
            </Button>
          </Box>
        </Box>

        {isLoading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Paper key={i}>
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="90%" />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('dictionaries.name')}</TableCell>
                    <TableCell>{t('dictionaries.description')}</TableCell>
                    <TableCell>{t('dictionaries.wordsCount')}</TableCell>
                    <TableCell>{t('dictionaries.createdAt')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                      <TableCell><Skeleton variant="text" width={40} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="right"><Skeleton variant="circular" width={32} height={32} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : dictionaries.length === 0 ? (
          <Paper>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {t('dictionaries.empty')}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateModalOpen(true)}
                sx={{ height: 40 }}
              >
                {t('dictionaries.createFirst')}
              </Button>
            </Box>
          </Paper>
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dictionaries.map((d) => (
                  <DictionaryCard
                    key={d.id}
                    dictionary={d}
                    onEdit={(dict) => {
                      setSelectedDictionary(dict)
                      setIsEditModalOpen(true)
                    }}
                    onDelete={handleDeleteDictionary}
                  />
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('dictionaries.name')}</TableCell>
                      <TableCell>{t('dictionaries.description')}</TableCell>
                      <TableCell>{t('dictionaries.wordsCount')}</TableCell>
                      <TableCell>{t('dictionaries.createdAt')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dictionaries.map((d) => (
                      <TableRow key={d.id} hover>
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={ROUTES.DICTIONARY_VIEW(d.id)}
                            underline="hover"
                            sx={{ fontWeight: 700 }}
                          >
                            {d.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {d.description || t('dictionaries.noDescription')}
                          </Typography>
                        </TableCell>
                        <TableCell>{d.words_count ?? 0}</TableCell>
                        <TableCell>{formatDateDMY(d.created_at)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => openActionsMenu(e, d)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                mt: 2,
                justifyContent: 'flex-end',
                minWidth: 0,
              }}
            >
              <TablePagination
                component="div"
                count={pagination.total}
                page={Math.max(0, pagination.currentPage - 1)}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage={isMobile ? '' : t('dictionaries.rows_per_page')}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t('dictionaries.of')} ${count !== -1 ? count : `${t('dictionaries.more_than')} ${to}`}`
                }
              />
            </Box>
          </>
        )}

        <Menu
          anchorEl={actionsAnchorEl}
          open={Boolean(actionsAnchorEl)}
          onClose={closeActionsMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              if (actionsDictionary) {
                closeActionsMenu()
                navigate(ROUTES.DICTIONARY_VIEW(actionsDictionary.id))
              }
            }}
          >
            <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.view')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (actionsDictionary) {
                setSelectedDictionary(actionsDictionary)
                setIsEditModalOpen(true)
                closeActionsMenu()
              }
            }}
          >
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.edit')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (actionsDictionary) {
                handleDeleteDictionary(actionsDictionary)
              }
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.delete')}
          </MenuItem>
        </Menu>

        <Dialog
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('dictionaries.create')}</DialogTitle>
          <DialogContent>
            <DictionaryForm
              onSubmit={handleCreateDictionary}
              onCancel={() => setIsCreateModalOpen(false)}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setSelectedDictionary(null)
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('common.edit')}</DialogTitle>
          <DialogContent>
            {selectedDictionary && (
              <DictionaryForm
                initialValues={selectedDictionary}
                onSubmit={handleEditDictionary}
                onCancel={() => {
                  setIsEditModalOpen(false)
                  setSelectedDictionary(null)
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
